<template>
    <div class="modal-container modal-container--wide">
        <form @submit.prevent="handleSubmit" class="modal-form">
            <div class="modal-header">
                <div class="icon-container">
                    <PhMapPin :size="24" class="text-gray-500" />
                </div>
                <div class="header-text">
                    <h2>{{ isEditing ? 'Edit' : 'Add' }} Address</h2>
                    <p>Company headquarters address</p>
                </div>
            </div>

            <div class="modal-body">
                <div class="form-row">
                    <label>Address Line 1</label>
                    <PrimeInputText
                        v-model="formData.address_line_1"
                        placeholder="123 Main Street"
                        class="w-full"
                    />
                </div>

                <div class="form-row">
                    <label>Address Line 2</label>
                    <PrimeInputText
                        v-model="formData.address_line_2"
                        placeholder="Suite 100 (optional)"
                        class="w-full"
                    />
                </div>

                <div class="form-grid">
                    <div class="form-row">
                        <label>City</label>
                        <PrimeInputText
                            v-model="formData.address_city"
                            placeholder="City"
                            class="w-full"
                        />
                    </div>

                    <div class="form-row">
                        <label>State/Region</label>
                        <PrimeInputText
                            v-model="formData.address_region"
                            placeholder="State"
                            class="w-full"
                        />
                    </div>
                </div>

                <div class="form-grid">
                    <div class="form-row">
                        <label>Postal Code</label>
                        <PrimeInputText
                            v-model="formData.address_postal_code"
                            placeholder="12345"
                            class="w-full"
                        />
                    </div>

                    <div class="form-row">
                        <label>Country</label>
                        <PrimeSelect
                            v-model="formData.address_country"
                            :options="countries"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select country"
                            class="w-full"
                        />
                    </div>
                </div>
            </div>

            <div class="modal-footer">
                <PrimeButton label="Cancel" @click="closeDialog" class="p-btn-root_secondary" :disabled="isLoading" />
                <PrimeButton :label="isEditing ? 'Update' : 'Add'" type="submit" :loading="isLoading" />
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import { PhMapPin } from '@phosphor-icons/vue';
import { businessService } from '~/services/business-service';
import { ModalNames } from '~/types/modals';

const dialogRef = getDialogRef(ModalNames.UPDATE_BUSINESS_ADDRESS);
const { showSuccess, showError } = useGlobalToast();
const isLoading = ref(false);

const formData = reactive({
    address_line_1: '',
    address_line_2: '',
    address_city: '',
    address_region: '',
    address_postal_code: '',
    address_country: 'US',
});

const countries = [
    { label: 'United States', value: 'US' },
    { label: 'Canada', value: 'CA' },
    { label: 'United Kingdom', value: 'GB' },
    { label: 'Australia', value: 'AU' },
    { label: 'Germany', value: 'DE' },
    { label: 'France', value: 'FR' },
    { label: 'Japan', value: 'JP' },
    { label: 'Other', value: 'OTHER' },
];

const companyId = computed(() => dialogRef.value?.data?.companyId);
const address = computed(() => dialogRef.value?.data?.address);
const isEditing = computed(() => !!address.value?.id);

onMounted(() => {
    if (address.value) {
        formData.address_line_1 = address.value.address_line_1 || '';
        formData.address_line_2 = address.value.address_line_2 || '';
        formData.address_city = address.value.address_city || '';
        formData.address_region = address.value.address_region || '';
        formData.address_postal_code = address.value.address_postal_code || '';
        formData.address_country = address.value.address_country || 'US';
    }
});

const closeDialog = () => {
    dialogRef.value.close();
};

const handleSubmit = async () => {
    if (!formData.address_line_1 || !formData.address_city || !formData.address_region) {
        showError('Error', 'Address, city, and state are required');
        return;
    }

    isLoading.value = true;
    try {
        const payload = {
            id: address.value?.id,
            ...formData,
        };

        await businessService.saveAddress(companyId.value, payload);
        showSuccess('Success', `Address ${isEditing.value ? 'updated' : 'added'}`);
        dialogRef.value.close({ updated: true });
    } catch (error: any) {
        showError('Error', error.message || 'Failed to save address');
    } finally {
        isLoading.value = false;
    }
};
</script>

<style lang="scss" scoped>
@use '../../../assets/styles/business-form-modal';

.modal-body {
    .form-grid {
        @apply grid grid-cols-2 gap-4;
    }
}
</style>
